import { defineStore } from 'pinia';
import { fetchBootstrap } from '@/entities/runtime/api';
import { mockBootstrap } from '@/entities/runtime/mock-bootstrap';
import type { BootstrapResponse, RuntimeStatus } from '@/entities/runtime/types';
import { validateRuntimeConfig } from '@/app/runtime-validator';
import { clientEnv } from '@/shared/config/env';
import { reportEvent } from '@/shared/monitoring/monitoring';
import { requestJson } from '@/shared/request/http-client';
import { persistAgentCode, storedAgentCode } from '@/shared/referral/invite-link';
import { initMetaPixel } from '@/shared/tracking/meta-pixel';
import { useSessionStore } from '@/stores/session-store';
import { loadThemeTokens } from '@/shared/theme/load-theme';
import { applyThemeTokens, type ThemeTokenMap } from '@/shared/theme/theme-tokens';
import type { SkinPreviewDraft } from '@/entities/runtime/preview-bridge';

interface RuntimeState {
  config: BootstrapResponse | null;
  errors: string[];
  previewDraft: SkinPreviewDraft | null;
  status: RuntimeStatus;
}

const canUseMockRuntime = () => clientEnv.appEnv !== 'production';

async function bootMetaPixel() {
  const session = useSessionStore();
  if (session.token) {
    if (session.player?.metaPixelId == null) {
      await session.fetchProfile();
    }
    if (session.player?.metaPixelId) {
      initMetaPixel(session.player.metaPixelId);
    }
    return;
  }
  const fromUrl = new URLSearchParams(window.location.search).get('agent')?.trim() || '';
  if (fromUrl) persistAgentCode(fromUrl);
  const agent = fromUrl || storedAgentCode();
  const query = new URLSearchParams({ siteId: clientEnv.siteId });
  if (agent) query.set('agent', agent);
  try {
    const result = await requestJson<{ pixelId?: string }>(`/v1/app/meta/pixel?${query.toString()}`);
    if (result.pixelId) initMetaPixel(result.pixelId);
  } catch {
    // 像素解析失败不影响进站
  }
}

export const useRuntimeStore = defineStore('runtime', {
  actions: {
    async boot() {
      if (this.status === 'booting' || this.status === 'ready') return;

      this.status = 'booting';
      this.errors = [];

      try {
        const runtime = await fetchBootstrap();
        await this.applyRuntime(runtime);
      } catch (error) {
        reportEvent({
          context: {
            error: error instanceof Error ? error.message : String(error),
            siteId: clientEnv.siteId,
          },
          event: 'runtime.bootstrap.failed',
          level: 'warn',
        });

        if (!canUseMockRuntime()) {
          this.status = 'error';
          this.errors = ['启动配置加载失败'];
          return;
        }

        await this.applyRuntime(mockBootstrap);
        this.status = 'offlineStable';
      }
    },
    async applyRuntime(runtime: BootstrapResponse) {
      const errors = validateRuntimeConfig(runtime);

      if (errors.length > 0) {
        this.status = 'incompatible';
        this.errors = errors;
        return;
      }

      const tokens = await loadThemeTokens(runtime.assets.themeTokenUrl);
      applyThemeTokens(tokens);
      this.config = runtime;
      this.status = this.status === 'offlineStable' ? 'offlineStable' : 'ready';
      void bootMetaPixel();

      reportEvent({
        context: {
          runtimeVersion: runtime.runtimeVersion,
          siteId: runtime.siteId,
          themeVersion: runtime.themeVersion,
        },
        event: 'runtime.bootstrap.ready',
        level: 'info',
      });
    },
    async applyPreviewRuntime(
      runtime: BootstrapResponse,
      themeTokens?: ThemeTokenMap,
      skinDraft?: SkinPreviewDraft,
    ) {
      await this.applyRuntime(runtime);

      if (this.status === 'incompatible' || this.status === 'error') {
        return;
      }

      if (themeTokens) {
        applyThemeTokens(themeTokens);
      }

      this.previewDraft = skinDraft ?? null;
      this.status = 'ready';
    },
  },
  getters: {
    currency: (state) => state.config?.currency ?? 'BRL',
    features: (state) => state.config?.features ?? mockBootstrap.features,
    isPreviewMode: (state) => Boolean(state.previewDraft),
    runtimeVersion: (state) => state.config?.runtimeVersion ?? '',
    siteId: (state) => state.config?.siteId ?? clientEnv.siteId,
  },
  state: (): RuntimeState => ({
    config: null,
    errors: [],
    previewDraft: null,
    status: 'idle',
  }),
});
